/** @file Hook: the dopesheet canvas — redraw on change, pointer gestures, wheel zoom/pan, fit per animation. */
import { useEffect, useMemo, useRef, useState, type PointerEvent, type RefObject } from 'react';
import { clamp } from '@pose2d/math/clamp';
import { timelineScale } from '@pose2d/editor-geometry/timelineScale';
import type { ScreenRect, TimelineScale } from '@pose2d/editor-geometry/types';
import type { EditorDeps } from '../../playback/editorDeps';
import { playheadStore } from '../../stores/playheadStore';
import { selectionStore } from '../../stores/selectionStore';
import { DOPE_PX } from './draw/dopeMetrics';
import { dopePalette } from './draw/dopePalette';
import { drawDopesheet } from './draw/drawDopesheet';
import { applyDopeEffects, type DopeTarget } from './interact/applyDopeEffects';
import { dopePointer, type DopeGesture, type DopePointer } from './interact/dopePointer';
import { dopeWheel } from './interact/dopeWheel';
import { dopeKeys } from './model/dopeKeys';
import type { DopeModel } from './model/dopesheetModel';

/** View state that changes at pointer rate: kept in a ref, never in React state. */
interface Live { scale: TimelineScale; scrollY: number; gesture: DopeGesture; drag: number; box: ScreenRect | null; dirty: boolean }

/** What DopesheetView spreads on the canvas and shares with the row headers. */
export interface DopeCanvas {
  ref: RefObject<HTMLCanvasElement | null>;
  scrollY: number;
  scroll: (y: number) => void;
  handlers: Record<'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel', (e: PointerEvent<HTMLCanvasElement>) => void>;
}

const maxScroll = (model: DopeModel, canvas: HTMLCanvasElement | null): number => Math.max(0, model.layout.height - ((canvas?.clientHeight ?? 0) - DOPE_PX.ruler));

/** Sizes the backing store to the CSS size times devicePixelRatio, then draws from the stores and `live`. */
function paint(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, model: DopeModel, live: Live, palette: ReturnType<typeof dopePalette>): void {
  const dpr = window.devicePixelRatio;
  const [w, h] = [canvas.clientWidth, canvas.clientHeight];
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) [canvas.width, canvas.height] = [Math.round(w * dpr), Math.round(h * dpr)];
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const { frame, loop } = playheadStore.getState();
  const marks = dopeKeys.marks(selectionStore.getState().keys);
  drawDopesheet(ctx, { layout: model.layout, scale: live.scale, width: w, height: h, scrollY: live.scrollY, frame, end: model.end, loop, marks, drag: live.drag, box: live.box }, palette);
}

/** rAF loop drawing only when something changed (store updates, resize, view edits set `dirty`). */
function useRedraw(ref: RefObject<HTMLCanvasElement | null>, model: DopeModel, live: Live): void {
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;
    const palette = dopePalette((name) => getComputedStyle(canvas).getPropertyValue(name));
    const dirty = (): void => { live.dirty = true; };
    const stops = [playheadStore.subscribe(dirty), selectionStore.subscribe(dirty)];
    const resize = new ResizeObserver(dirty);
    resize.observe(canvas);
    let handle = 0;
    const frame = (): void => {
      if (live.dirty) paint(canvas, ctx, model, live, palette);
      live.dirty = false;
      handle = requestAnimationFrame(frame);
    };
    dirty();
    handle = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(handle); resize.disconnect(); for (const stop of stops) stop(); };
  }, [ref, model, live]);
}

/** Wheel over the canvas (non-passive, so Ctrl+wheel zooms the time axis instead of the page). */
function useWheel(ref: RefObject<HTMLCanvasElement | null>, model: DopeModel, live: Live, scroll: (y: number) => void): void {
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const next = dopeWheel(live.scale, live.scrollY, maxScroll(model, canvas), { x: e.offsetX, dx: e.deltaX, dy: e.deltaY, mod: e.ctrlKey || e.metaKey, shift: e.shiftKey });
      live.scale = next.scale;
      scroll(next.scrollY);
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => { canvas.removeEventListener('wheel', onWheel); };
  }, [ref, model, live, scroll]);
}

/** The DopeTarget over the stores, `live` and the editor dispatch. */
function targetOf(live: Live, scroll: (y: number) => void, deps: EditorDeps): DopeTarget {
  return {
    doc: () => deps.doc(),
    selected: () => selectionStore.getState().keys,
    select: (keys) => { selectionStore.setState({ keys }); },
    setDrag: (delta) => { live.drag = delta; },
    setBox: (rect) => { live.box = rect; },
    pan: (dx, dy) => { live.scale = timelineScale.pan(live.scale, dx); scroll(live.scrollY - dy); },
    dispatch: (calls) => { deps.dispatch(calls); },
  };
}

/** Owns the canvas view state; `model` comes from dopesheetModel, `deps` from editorDeps. */
export function useDopesheetCanvas(model: DopeModel, deps: EditorDeps): DopeCanvas {
  const ref = useRef<HTMLCanvasElement>(null);
  const live = useRef<Live>({ scale: { frame0: -1, pxPerFrame: 12 }, scrollY: 0, gesture: { kind: 'idle' }, drag: 0, box: null, dirty: true }).current;
  const [scrollY, setScrollY] = useState(0);
  const scroll = useMemo(() => (y: number): void => {
    live.scrollY = clamp(y, 0, maxScroll(model, ref.current));
    live.dirty = true;
    setScrollY(live.scrollY);
  }, [live, model]);
  const { animation, end } = model;
  useEffect(() => {
    live.scale = timelineScale.fit(0, end, ref.current?.clientWidth ?? 600, 16);
    live.dirty = true;
    // Refit only when another animation opens; a length edit keeps the user's zoom (hence no `end` dependency).
  }, [live, animation]);
  useRedraw(ref, model, live);
  useWheel(ref, model, live, scroll);
  const target = useMemo(() => targetOf(live, scroll, deps), [live, scroll, deps]);
  const handle = (type: DopePointer['type']) => (e: PointerEvent<HTMLCanvasElement>): void => {
    const box = e.currentTarget.getBoundingClientRect();
    if (type === 'down') e.currentTarget.setPointerCapture(e.pointerId);
    const ev: DopePointer = { type, x: e.clientX - box.left, y: e.clientY - box.top, button: e.button, shift: e.shiftKey, mod: e.ctrlKey || e.metaKey, alt: e.altKey };
    const at = { layout: model.layout, scale: live.scale, scrollY: live.scrollY, animation: model.animation, groups: model.groups, selected: selectionStore.getState().keys };
    const step = dopePointer(live.gesture, ev, at);
    live.gesture = step.gesture;
    applyDopeEffects(step.effects, target);
    live.dirty = true;
  };
  const cancel = (): void => { Object.assign(live, { gesture: { kind: 'idle' }, drag: 0, box: null, dirty: true }); };
  return { ref, scrollY, scroll, handlers: { onPointerDown: handle('down'), onPointerMove: handle('move'), onPointerUp: handle('up'), onPointerCancel: cancel } };
}
